import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type { GeoLocation } from 'components/GeoLocationComponent/buildGeoLocationFromGeoJson';
import type { Geometry } from 'geojson';
import type { EscalationPlan } from 'components/MapPage/CorporateViewTabs/NotificationsTab/escalationPlanModel';
import type { ResponseOption } from 'components/MapPage/CorporateViewTabs/NotificationsTab/responseOptions';
import type { ChecklistFileAttachment } from 'components/IAP/actionChecklistModel';
import type {
  ActionAttachment,
  ActionHazard,
  ActionResourceRequirement,
} from 'components/IAP/actionTicket/actionTicketModel';
import {
  DEFAULT_EVENT_NAME_TEMPLATE,
  renderTemplatePreview,
  toDisplayTemplate,
} from 'components/MapPage/CorporateViewTabs/EventsTab/eventNameTemplatePreview';
import config from 'constants/apiConfig';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from 'slices/commonSelectors';

export type OrgEventAutomationLogic = 'all' | 'any';

export type OrgEventAorAssignmentStrategy =
  | 'fixed'
  | 'from_resources'
  /** AORs matched by `aor_intersect` conditions on this rule. */
  | 'from_impacted_aors'
  | 'none';

export type OrgEventAorAssignment = {
  strategy: OrgEventAorAssignmentStrategy;
  aorIds?: string[];
};

export type OrgEventAutomationSource = {
  /**
   * `external_api` reads a non-ArcGIS feed whose `serviceUrl` is a `feed://`
   * URI.
   */
  type: 'arcgis_feature' | 'external_api';
  /**
   * `active_hurricanes_cone` tropical preset, `custom` ArcGIS URL,
   * `sensor_alpha` placeholder feed, or `arcgis_layer` picked layer.
   */
  preset: string;
  serviceUrl: string;
  intensityLayerUrl?: string;
  layerLabel?: string;
  featureIdField?: string;
  featureNameField?: string;
  /** `asset` opens one event per impacted asset rather than per feature. */
  occurrenceBy?: 'feature' | 'asset';
  integrationKey?: 'sensor_alpha' | string;
  /** Linked row in `external_layers` when picked from the catalog. */
  externalLayerId?: string;
  /** FeatureServer / MapServer sublayer index when applicable. */
  sublayerId?: number;
  /** Auth mode for automation readiness UI. */
  authType?: 'apiKey' | 'userAuth' | 'public';
  /** Snapshot of the chosen attribute field for offline render / later eval. */
  fieldSnapshot?: {
    name: string;
    alias: string;
    type: string;
    codedValues?: { name: string; code: string | number }[];
  };
};

export type AttributeCondition = {
  id?: string;
  source?: OrgEventAutomationSource;
  kind: 'attribute';
  field: string;
  operator: '>' | '>=' | '<' | '<=' | '==';
  value: string;
};

/** 0 / omitted = must overlap. >0 = within this many kilometers. */
export function sanitizeProximityKm(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

export function formatSpatialMatchPhrase(proximityKm?: unknown): string {
  const km = sanitizeProximityKm(proximityKm);
  return km > 0 ? `within ${km} km of the feature` : 'overlap';
}

export type ResourceIntersectCondition = {
  id?: string;
  source?: OrgEventAutomationSource;
  kind: 'resource_intersect';
  minCount: number;
  geometryFrom: 'cone' | 'feature';
  /** 0 / omitted = must overlap. >0 = within this many kilometers. */
  proximityKm?: number;
  /**
   * When set, only this asset's location is tested. Empty string means the
   * author has not picked one yet, and the condition does not match every
   * asset.
   */
  resourceSetId?: string;
  /** Display name captured when the asset was picked. */
  resourceName?: string;
};

/**
 * Fires when the hazard/feature geometry overlaps selected org AOR polygons.
 * Distinct from `event_template.aorAssignment`, which only stamps AOR names
 * onto the created event.
 */
export type AorIntersectCondition = {
  id?: string;
  source?: OrgEventAutomationSource;
  kind: 'aor_intersect';
  /** Empty list never passes. */
  aorIds: string[];
  /** How many of the selected AORs must intersect. */
  minCount: number;
  geometryFrom: 'cone' | 'feature';
  /** 0 / omitted = must overlap. >0 = within this many kilometers. */
  proximityKm?: number;
};

export type OrgEventAutomationCondition =
  | AttributeCondition
  | ResourceIntersectCondition
  | AorIntersectCondition;

export type OrgEventAutomationLogicJoin = 'and' | 'or';

export type OrgEventAutomationConditionBlock = {
  type: 'condition';
  conditionId: string;
  joinWithPrevious?: OrgEventAutomationLogicJoin;
};

export type OrgEventAutomationGroupBlock = {
  type: 'group';
  id: string;
  joinWithPrevious?: OrgEventAutomationLogicJoin;
  internalJoin: OrgEventAutomationLogicJoin;
  conditionIds: string[];
};

export type OrgEventAutomationLogicBlock =
  | OrgEventAutomationConditionBlock
  | OrgEventAutomationGroupBlock;

export type OrgEventTemplate = {
  severity?: string;
  type?: string | null;
  region?: string | null;
  status?: string;
  /** `{token}` template for the created event's name. */
  eventName?: string | null;
  thresholdDescription?: string | null;
  summaryTemplate?: string | null;
  /** Pratus writes the event summary instead of copying the description. */
  aiSummary?: boolean;
  aorAssignment?: OrgEventAorAssignment;
  /** When omitted, a saved rule is treated as on. */
  preventDuplicateEvents?: boolean;
  /** Hours after the last fire during which a match updates that event. */
  duplicateWindowHours?: number;
  /**
   * When a later check updates the existing event, notify recipients. Omitted
   * means notify, matching rules saved before this choice existed.
   */
  notifyOnDataChange?: boolean;
};

export type OrgEventNotifyConfig = {
  notificationTypes?: string[];
  /**
   * Channels for "event updated" sends. Unset means the create channels without
   * SMS and voice.
   */
  updateNotificationTypes?: string[];
  /** Least time between "event updated" sends for one event. Unset means 60. */
  updateMinIntervalMinutes?: number;
  userIds?: string[];
  teamIds?: string[];
  /** `roleId|scopeId` keys. Resolved to people when the rule sends. */
  roleScopeKeys?: string[];
  priority?: string;
  /** Recipients are asked to confirm they received the notification. */
  confirmReceipt?: boolean;
  titleTemplate?: string;
  bodyTemplate?: string;
  emailSubject?: string | null;
  emailMessage?: string | null;
  smsMessage?: string | null;
  teamsMessage?: string | null;
  phoneMessage?: string | null;
  appMessage?: string | null;
  /** ISO time. Omitted or null sends as soon as the event is created. */
  scheduledDate?: string | null;
  audienceGeometry?: Geometry | null;
  audienceAorItemIds?: string[];
  audienceLocationMatch?: 'home_aor' | 'current_location' | null;
  responseOptions?: ResponseOption[] | null;
  escalationPlan?: EscalationPlan | null;
  incidentId?: string | null;
  /** Metadata tags, distinct from the audience filter. */
  aors?: string[];
};

export type ChecklistItemKind = 'org_action' | 'send_notification';

/**
 * `userIds` and `teamIds` prefill the compose sheet an operator opens from the
 * event checklist; the automation itself never sends these.
 */
export type OrgEventChecklistNotifyTemplate = Pick<
  OrgEventNotifyConfig,
  | 'notificationTypes'
  | 'priority'
  | 'titleTemplate'
  | 'bodyTemplate'
  | 'emailSubject'
  | 'emailMessage'
  | 'smsMessage'
  | 'teamsMessage'
  | 'phoneMessage'
  | 'appMessage'
  | 'userIds'
  | 'teamIds'
>;

/** Defaults applied to the org action created when the rule fires. */
export type OrgEventChecklistOrgActionTemplate = {
  /** `ACTION_STATUS_OPTIONS` value; defaults to `Planned`. */
  status?: string;
  /** POC user_guid; null leaves the action unassigned. */
  poc?: string | null;
  briefedPoc?: boolean;
  checklist?: Array<{
    label: string;
    checked?: boolean;
    note?: string;
    attachments?: ChecklistFileAttachment[];
    responsible_individual_id?: string;
  }>;
  priority?: string;
  assigneeKind?: 'user' | 'team' | 'position' | 'role' | null;
  assigneeId?: string | null;
  startDate?: string | null;
  targetDate?: string | null;
  geolocation?: GeoLocation | null;
  symbology?: string | null;
  aorId?: string | null;
  /** Every Responsible AOR copied onto the task template. `aorId` is the first. */
  aorIds?: string[];
  reportingLocation?: string | null;
  requestedArrivalTime?: string | null;
  resourceRequirements?: ActionResourceRequirement[];
  resourceRequirementsNa?: boolean;
  hazards?: ActionHazard[];
  attachments?: ActionAttachment[];
  /** Sibling checklist item ids; remapped to action ids on materialize. */
  dependsOn?: string[];
};

export type OrgEventChecklistItemDef = {
  id: string;
  kind: ChecklistItemKind;
  label: string;
  description?: string;
  notify?: OrgEventChecklistNotifyTemplate;
  orgAction?: OrgEventChecklistOrgActionTemplate;
};

export type OrgEventChecklistConfig = {
  items?: OrgEventChecklistItemDef[];
  /** @deprecated Legacy — attach existing action IDs without materializing */
  actionIds?: string[];
};

export type OrgEventAutomationRule = {
  id: string;
  group_guid: string;
  name: string;
  enabled: boolean;
  logic: OrgEventAutomationLogic | string;
  source: OrgEventAutomationSource;
  conditions: OrgEventAutomationCondition[];
  logic_blocks?: OrgEventAutomationLogicBlock[];
  event_template: OrgEventTemplate;
  notify_on_create: boolean;
  notify_config: OrgEventNotifyConfig;
  checklist_on_create?: boolean;
  checklist_config?: OrgEventChecklistConfig;
  launch_scenario_on_create?: boolean;
  scenario_id?: string | null;
  dedupe_key_template: string;
  last_fired_dedupe_key: string | null;
  last_fired_at?: string | null;
  last_fired_event_id?: string | null;
  scheduler_name: string | null;
  repeat_pattern: string;
  created_by_user_guid: string | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
};

export type CreateOrgEventAutomationRulePayload = {
  group_guid: string;
  name: string;
  enabled?: boolean;
  logic?: OrgEventAutomationLogic;
  source?: OrgEventAutomationSource;
  conditions?: OrgEventAutomationCondition[];
  logic_blocks?: OrgEventAutomationLogicBlock[];
  event_template?: OrgEventTemplate;
  notify_on_create?: boolean;
  notify_config?: OrgEventNotifyConfig;
  checklist_on_create?: boolean;
  checklist_config?: OrgEventChecklistConfig;
  launch_scenario_on_create?: boolean;
  scenario_id?: string | null;
  dedupe_key_template?: string;
  repeat_pattern?: string;
  created_by_user_guid?: string | null;
};

export type UpdateOrgEventAutomationRulePayload = Partial<
  Omit<CreateOrgEventAutomationRulePayload, 'group_guid'>
> & {
  clear_last_fired?: boolean;
};

export type RunAutomationRuleResult = {
  fired: boolean;
  skippedDuplicate: boolean;
  reason?: string;
  orgEventId?: string;
  dedupeKey?: string;
  stormName?: string;
  featureName?: string;
  resourceCount?: number;
  /** Why a data feed could not be read, when `reason` is `feed_unavailable`. */
  detail?: string;
  /**
   * Data feed problems that did not stop the check, such as assets with no
   * forecast.
   */
  feedWarnings?: string[];
};

/** One ArcGIS feature that caused an automation event. */
export type OrgEventTriggeringItem = {
  id: string;
  name: string;
  geometry: Geometry;
  resourceSetIds: string[];
  resourceNames: string[];
  aorNames: string[];
  serviceUrl?: string;
  featureIdField?: string;
};

export type AutomationRulePreviewChecklistItem = {
  kind: ChecklistItemKind;
  label: string;
  description?: string;
  status?: string;
  poc?: string | null;
  subTaskCount?: number;
  channels?: string[];
  priority?: string;
  title?: string;
  body?: string;
  recipientCount?: number;
};

/** What the rule would produce, rendered from live data without persisting. */
export type AutomationRulePreview = {
  event: {
    name: string;
    type: string;
    severity: string;
    status: string;
    region: string | null;
    summary: string;
    thresholdDescription: string;
    lon: number | null;
    lat: number | null;
  };
  checklist: AutomationRulePreviewChecklistItem[];
  notification: {
    channels: string[];
    priority: string;
    title: string;
    body: string;
    recipientCount: number;
  } | null;
  scenario: {
    name: string;
    recipientCount: number;
    actionLabels: string[];
    emptyAudience: boolean;
  } | null;
  matchedFeatureName: string;
  /** ArcGIS features that passed, so the event can list and zoom to them. */
  triggeringItems?: OrgEventTriggeringItem[];
  resourceCount: number;
  impactedAorNames: string[];
  isUpdate: boolean;
  wouldSkipDuplicate: boolean;
  resourceChecklists?: {
    wouldCreateItemCount: number;
    assetsWithCoas: Array<{
      resourceSetId: string;
      name: string;
      itemCount: number;
    }>;
    assetsAtRiskWithoutCoas: Array<{
      resourceSetId: string;
      name: string;
    }>;
  };
};

export type PreviewAutomationRuleResult = RunAutomationRuleResult & {
  preview?: AutomationRulePreview;
};

export const ACTIVE_HURRICANES_GLOBAL_LABEL =
  'Active Hurricanes / Tropical Cyclones (Global)';

export const CURRENT_WILDFIRE_INCIDENTS_LABEL = 'Current Wildfire Incidents';

export const CURRENT_WILDFIRE_INCIDENTS_URL =
  'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/USA_Wildfires_v1/FeatureServer/0';

export const CURRENT_AIR_QUALITY_LABEL = 'Current air quality (AirNow)';

export const CURRENT_AIR_QUALITY_URL =
  'https://services.arcgis.com/cJ9YHowT8TU7DUyn/arcgis/rest/services/Air%20Now%20Current%20Monitor%20Data%20Public/FeatureServer/0';

export const DEFAULT_ACTIVE_HURRICANES_SOURCE: OrgEventAutomationSource = {
  type: 'arcgis_feature',
  preset: 'active_hurricanes_cone',
  serviceUrl:
    'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Active_Hurricanes_v1/FeatureServer/4',
  intensityLayerUrl:
    'https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/Active_Hurricanes_v1/FeatureServer/1',
  layerLabel: ACTIVE_HURRICANES_GLOBAL_LABEL,
};

export const DEFAULT_WILDFIRE_INCIDENTS_SOURCE: OrgEventAutomationSource = {
  type: 'arcgis_feature',
  preset: 'wildfire_incidents',
  serviceUrl: CURRENT_WILDFIRE_INCIDENTS_URL,
  layerLabel: CURRENT_WILDFIRE_INCIDENTS_LABEL,
};

export const DEFAULT_AIR_QUALITY_SOURCE: OrgEventAutomationSource = {
  type: 'arcgis_feature',
  preset: 'air_quality',
  authType: 'public',
  serviceUrl: CURRENT_AIR_QUALITY_URL,
  layerLabel: CURRENT_AIR_QUALITY_LABEL,
  featureNameField: 'SiteName',
};

export const DEFAULT_CUSTOM_SOURCE: OrgEventAutomationSource = {
  type: 'arcgis_feature',
  preset: 'custom',
  serviceUrl: '',
  layerLabel: '',
};

export const DEFAULT_SENSOR_ALPHA_SOURCE: OrgEventAutomationSource = {
  type: 'arcgis_feature',
  preset: 'sensor_alpha',
  serviceUrl: '',
  layerLabel: 'Sensor Alpha',
  integrationKey: 'sensor_alpha',
};

export const DEFAULT_ARCGIS_LAYER_SOURCE: OrgEventAutomationSource = {
  type: 'arcgis_feature',
  preset: 'arcgis_layer',
  serviceUrl: '',
  layerLabel: '',
  authType: 'public',
};

export const DEFAULT_TROPICAL_CONDITIONS: OrgEventAutomationCondition[] = [
  {
    kind: 'attribute',
    field: 'STORMTYPE',
    operator: '>=',
    value: 'TS',
  },
  {
    kind: 'resource_intersect',
    minCount: 1,
    geometryFrom: 'cone',
  },
];

export const DEFAULT_SENSOR_ALPHA_CONDITIONS: OrgEventAutomationCondition[] = [
  {
    kind: 'attribute',
    field: 'sensor_value',
    operator: '>=',
    value: '5',
  },
];

export function defaultCustomConditions(
  minCount = 1
): OrgEventAutomationCondition[] {
  return [
    {
      kind: 'resource_intersect',
      minCount,
      geometryFrom: 'feature',
    },
  ];
}

export function isTropicalSourcePreset(
  source: OrgEventAutomationSource | undefined
): boolean {
  const preset = source?.preset ?? '';
  return (
    preset === 'active_hurricanes_cone' ||
    preset === 'tropical_active_hurricanes' ||
    (!preset && Boolean(source?.intensityLayerUrl))
  );
}

export function isSensorAlphaSourcePreset(
  source: OrgEventAutomationSource | undefined
): boolean {
  if (!source) return false;
  return (
    source.preset === 'sensor_alpha' || source.integrationKey === 'sensor_alpha'
  );
}

export function isArcGisLayerSourcePreset(
  source: OrgEventAutomationSource | undefined
): boolean {
  if (!source) return false;
  return source.preset === 'arcgis_layer';
}

export const EVENT_CATEGORY_OPTIONS = [
  { label: 'Tropical Storm', value: 'Tropical Storm' },
  { label: 'Flood', value: 'Flood' },
  { label: 'Wildfire', value: 'Wildfire' },
  { label: 'Infrastructure', value: 'Infrastructure' },
  { label: 'Threshold', value: 'Threshold' },
  { label: 'Other', value: 'Other' },
] as const;

export const orgEventAutomationKeys = {
  all: ['org-event-automation-rules'] as const,
  list: (groupGuid: string) =>
    [...orgEventAutomationKeys.all, 'list', groupGuid] as const,
};

export function useOrgEventAutomationRules(options?: { enabled?: boolean }) {
  const groupGuid = useSelector(selectGroupGuid);
  return useQuery({
    queryKey: orgEventAutomationKeys.list(groupGuid ?? ''),
    queryFn: async () => {
      const resp = await axios.get<OrgEventAutomationRule[]>(
        config.apiGateway.org_event_automation_rules,
        { params: { groupGuid } }
      );
      return resp.data ?? [];
    },
    enabled: !!groupGuid && (options?.enabled ?? true),
  });
}

export function useCreateOrgEventAutomationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateOrgEventAutomationRulePayload) => {
      const resp = await axios.post<OrgEventAutomationRule>(
        config.apiGateway.org_event_automation_rules,
        payload
      );
      return resp.data;
    },
    onSuccess: (rule) => {
      void queryClient.invalidateQueries({
        queryKey: orgEventAutomationKeys.list(rule.group_guid),
      });
    },
  });
}

export function useUpdateOrgEventAutomationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: UpdateOrgEventAutomationRulePayload & { id: string }) => {
      const resp = await axios.patch<OrgEventAutomationRule>(
        `${config.apiGateway.org_event_automation_rules}/${id}`,
        payload
      );
      return resp.data;
    },
    onSuccess: (rule) => {
      void queryClient.invalidateQueries({
        queryKey: orgEventAutomationKeys.list(rule.group_guid),
      });
    },
  });
}

export function useDeleteOrgEventAutomationRule() {
  const queryClient = useQueryClient();
  const groupGuid = useSelector(selectGroupGuid);
  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(
        `${config.apiGateway.org_event_automation_rules}/${id}`
      );
      return id;
    },
    onSuccess: () => {
      if (groupGuid) {
        void queryClient.invalidateQueries({
          queryKey: orgEventAutomationKeys.list(groupGuid),
        });
      }
    },
  });
}

export function useRunOrgEventAutomationRule() {
  const queryClient = useQueryClient();
  const groupGuid = useSelector(selectGroupGuid);
  return useMutation({
    mutationFn: async (
      input: string | { id: string; useDemoStorms?: boolean }
    ) => {
      const id = typeof input === 'string' ? input : input.id;
      const useDemoStorms =
        typeof input === 'string' ? false : Boolean(input.useDemoStorms);
      const resp = await axios.post<RunAutomationRuleResult>(
        `${config.apiGateway.org_event_automation_rules}/${id}/run`,
        { useDemoStorms }
      );
      return resp.data;
    },
    onSuccess: () => {
      if (groupGuid) {
        void queryClient.invalidateQueries({
          queryKey: orgEventAutomationKeys.list(groupGuid),
        });
        void queryClient.invalidateQueries({ queryKey: ['org-events'] });
        void queryClient.invalidateQueries({
          queryKey: ['userNotifications', groupGuid],
        });
      }
    },
  });
}

/**
 * Dry-run the rule against live data. Writes nothing and sends nothing, so it
 * invalidates no caches.
 */
export function usePreviewOrgEventAutomationRule() {
  return useMutation({
    mutationFn: async (input: {
      id: string;
      draft?: UpdateOrgEventAutomationRulePayload;
      useDemoStorms?: boolean;
    }) => {
      const resp = await axios.post<PreviewAutomationRuleResult>(
        `${config.apiGateway.org_event_automation_rules}/${input.id}/preview`,
        {
          draft: input.draft,
          useDemoStorms: Boolean(input.useDemoStorms),
        }
      );
      return resp.data;
    },
  });
}

export type ReviseAutomationRuleDraftResult = {
  rule?: Omit<
    CreateOrgEventAutomationRulePayload,
    'group_guid' | 'created_by_user_guid'
  >;
  reply: string;
  errors: string[];
};

/** In-modal PRATUS AI chat over an unsaved rule draft. Nothing is saved. */
export function useReviseAutomationRuleDraft() {
  const groupGuid = useSelector(selectGroupGuid);
  return useMutation({
    mutationFn: async (input: {
      rule: Omit<
        CreateOrgEventAutomationRulePayload,
        'group_guid' | 'created_by_user_guid'
      >;
      message: string;
      history?: { role: 'user' | 'assistant'; content: string }[];
    }) => {
      const resp = await axios.post<ReviseAutomationRuleDraftResult>(
        config.apiGateway.reviseAutomationRuleDraft,
        { ...input, group_guid: groupGuid }
      );
      return resp.data;
    },
  });
}

/**
 * `editor` points at the form rendered underneath the sentence, so it drops the
 * event name and category. `list` spells them out because nothing follows it.
 */
export type AutomationSummaryVariant = 'list' | 'editor';

export function formatAutomationRuleSummary(
  rule: {
    name: string;
    logic: string;
    conditions: OrgEventAutomationCondition[];
    event_template?: OrgEventTemplate;
    notify_on_create?: boolean;
    source?: OrgEventAutomationSource;
  },
  variant: AutomationSummaryVariant = 'list'
): string {
  const intensity = rule.conditions.find((c) => c.kind === 'attribute');
  const spatial = rule.conditions.find((c) => c.kind === 'resource_intersect');
  const tropical = isTropicalSourcePreset(rule.source);
  const sensor = isSensorAlphaSourcePreset(rule.source);
  const layerLabel =
    rule.source?.layerLabel?.trim() ||
    rule.source?.serviceUrl?.trim() ||
    (tropical
      ? ACTIVE_HURRICANES_GLOBAL_LABEL
      : sensor
        ? 'Sensor Alpha'
        : 'ArcGIS layer');
  const feedLabel = sensor
    ? 'Sensor Alpha'
    : tropical
      ? 'Active Hurricanes'
      : layerLabel;
  const parts: string[] = [];
  if (intensity && intensity.kind === 'attribute') {
    parts.push(
      tropical
        ? `storm ${intensity.field} ${intensity.operator} ${intensity.value}`
        : `${intensity.field} ${intensity.operator} ${intensity.value}`
    );
  }
  if (spatial && spatial.kind === 'resource_intersect') {
    parts.push(
      `≥ ${spatial.minCount} resource(s) ${formatSpatialMatchPhrase(
        spatial.proximityKm
      )}`
    );
  }
  const aorSpatial = rule.conditions.find((c) => c.kind === 'aor_intersect');
  if (aorSpatial && aorSpatial.kind === 'aor_intersect') {
    const selected = aorSpatial.aorIds?.length ?? 0;
    const km = sanitizeProximityKm(aorSpatial.proximityKm);
    parts.push(
      km > 0
        ? `≥ ${aorSpatial.minCount} of ${selected} selected AOR(s) ${formatSpatialMatchPhrase(
            aorSpatial.proximityKm
          )}`
        : `≥ ${aorSpatial.minCount} of ${selected} selected AOR(s) overlap ${
            tropical ? 'the forecast cone' : layerLabel
          }`
    );
  }
  const join = rule.logic === 'any' ? ' or ' : ' and ';
  const criteria = parts.length ? parts.join(join) : 'configured criteria';

  if (variant === 'editor') {
    return `When Pratus reads ${feedLabel}, if ${criteria}, then auto-create an event with the below characteristics.`;
  }

  const eventName = renderTemplatePreview(
    toDisplayTemplate(
      rule.event_template?.eventName || DEFAULT_EVENT_NAME_TEMPLATE
    ),
    { ruleName: rule.name }
  );
  const category = rule.event_template?.type
    ? ` in the ${rule.event_template.type} category`
    : '';
  const notify = rule.notify_on_create
    ? ', and notify selected users/roles'
    : '';
  return `When Pratus reads ${feedLabel}, if ${criteria}, create "${eventName}"${category}${notify}.`;
}
